
import { defineComponent, ref, onMounted, computed, toRefs } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"
import { ElMessage } from "element-plus"
import { Plus } from '@element-plus/icons-vue'
import type { ElForm } from "element-plus"
import ItemsManager from "./ItemsManager.vue"
import RewardsManager from "./RewardsManager.vue"

type FormInstance = InstanceType<typeof ElForm>;

interface MatchPoolForm {
  name: string;
  image: string | null;
  description: string | null;
  price: number;
  default_quantity: number;
  field_size: number;
  status: string;
  box_type: string;
  box_category_id: number | null;
}

interface BoxCategory {
  id: number;
  name: string;
  category_type: number;
}

export default defineComponent({
  components: {
    Plus,
    ItemsManager,
    RewardsManager
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<FormInstance>()

    const { isEdit } = toRefs(props)
    const isNew = !isEdit.value || !route.params.id
    const savedPoolId = ref<number | null>(isNew ? null : Number(route.params.id))

    onMounted(() => {
      if (isNew) {
        setCurrentPageBreadcrumbs("新建池子", ["对对碰系统", "池子管理;match_pools"])
      } else {
        setCurrentPageBreadcrumbs("编辑池子", ["对对碰系统", "池子管理;match_pools"])
      }
    })

    const matchPool = ref<MatchPoolForm>({
      name: '',
      image: null,
      description: null,
      price: 100,
      default_quantity: 9,
      field_size: 9,
      status: 'draft',
      box_type: 'cash',
      box_category_id: null
    })

    // 获取对对碰类型的目录列表
    const matchCategories = ref<BoxCategory[]>([])

    const fetchMatchCategories = () => {
      store
        .dispatch(Actions.GET_BOX_CATEGORIES, { category_type: 2 })
        .then(() => {
          matchCategories.value = store.getters.currentBoxCategories
        })
        .catch(() => {
          // ignore
        })
    }

    // 组件加载时获取目录列表
    fetchMatchCategories()

    const rules = ref({
      name: [
        { required: true, message: '请输入池子名称', trigger: 'blur' },
        { min: 1, max: 100, message: '名称长度需在 1-100 之间', trigger: 'blur' }
      ],
      price: [
        { required: true, message: '请输入价格', trigger: 'blur' }
      ],
      default_quantity: [
        { required: true, message: '请输入默认牌数', trigger: 'blur' }
      ],
      field_size: [
        { required: true, message: '请输入场上牌数', trigger: 'blur' }
      ],
      box_type: [
        { required: true, message: '请选择价格类型', trigger: 'change' }
      ],
      status: [
        { required: true, message: '请选择状态', trigger: 'change' }
      ]
    })

    // 图片上传相关（base64 方式，随表单提交）
    const imageUrl = ref('')

    const onImageChange = (file: { raw: File }) => {
      const rawFile = file.raw
      if (!rawFile.type.startsWith('image/')) {
        ElMessage.error('只能上传图片文件!')
        return
      }
      if (rawFile.size / 1024 / 1024 > 2) {
        ElMessage.error('图片大小不能超过 2MB!')
        return
      }
      const reader = new FileReader()
      reader.readAsDataURL(rawFile)
      reader.onload = (e) => {
        imageUrl.value = e.target?.result?.toString() || ''
        matchPool.value.image = imageUrl.value
      }
    }

    // 加载详情（编辑模式）
    if (!isNew) {
      store
        .dispatch(Actions.GET_MATCH_POOL, route.params.id)
        .then(() => {
          const data = store.getters.currentMatchPool
          matchPool.value = {
            name: data.name,
            image: data.image,
            description: data.description,
            price: data.price,
            default_quantity: data.default_quantity || 9,
            field_size: data.field_size,
            status: data.status,
            box_type: data.box_type,
            box_category_id: data.box_category_id
          }
          if (data.image) {
            imageUrl.value = data.image
          }
        })
        .catch(() => {
          ElMessage.error('获取池子详情失败')
        })
    }

    const onSubmit = (formEl: FormInstance | undefined) => {
      if (!formEl) return

      formEl.validate((valid) => {
        if (valid) {
          const params = { ...matchPool.value }

          if (isNew) {
            store
              .dispatch(Actions.CREATE_MATCH_POOL, params)
              .then((data: { success: boolean; error_msg?: string; match_pool?: { id: number } }) => {
                if (data.success) {
                  ElMessage.success('创建成功')
                  // 创建成功后跳转到编辑页面，以便配置赏品和奖励
                  if (data.match_pool?.id) {
                    router.replace({ name: 'match_pools_edit', params: { id: data.match_pool.id } })
                    savedPoolId.value = data.match_pool.id
                  } else {
                    router.push({ name: 'match_pools' })
                  }
                } else {
                  ElMessage.error(data.error_msg || '创建失败')
                }
              })
              .catch(() => {
                ElMessage.error('创建失败')
              })
          } else {
            store
              .dispatch(Actions.UPDATE_MATCH_POOL, {
                id: route.params.id,
                values: params
              })
              .then((data: { success: boolean; error_msg?: string }) => {
                if (data.success) {
                  ElMessage.success('更新成功')
                } else {
                  ElMessage.error(data.error_msg || '更新失败')
                }
              })
              .catch(() => {
                ElMessage.error('更新失败')
              })
          }
        }
      })
    }

    const onCancel = () => {
      router.push({ name: 'match_pools' })
    }

    const onItemsUpdate = () => {
      // 赏品配置更新后可以刷新池子详情
      if (savedPoolId.value) {
        store.dispatch(Actions.GET_MATCH_POOL, savedPoolId.value)
      }
    }

    const onRewardsUpdate = () => {
      // 奖励配置更新后可以刷新池子详情
      if (savedPoolId.value) {
        store.dispatch(Actions.GET_MATCH_POOL, savedPoolId.value)
      }
    }

    return {
      formRef,
      matchPool,
      rules,
      isNew,
      savedPoolId,
      matchCategories,
      imageUrl,
      onImageChange,
      onSubmit,
      onCancel,
      onItemsUpdate,
      onRewardsUpdate
    }
  }
})
